import { useToast } from '@/components/ui/toast';

// Toast utility functions for common use cases
export const useToastHelpers = () => {
  const { addToast } = useToast();

  const showSuccess = (message: string, duration: number = 5000) => {
    addToast({
      message,
      type: 'success',
      duration,
    });
  };

  const showError = (message: string, duration: number = 5000) => {
    addToast({
      message,
      type: 'error',
      duration,
    });
  };

  const showWarning = (message: string, duration: number = 5000) => {
    addToast({
      message,
      type: 'warning',
      duration,
    });
  };

  const showInfo = (message: string, duration: number = 5000) => {
    addToast({
      message,
      type: 'info',
      duration,
    });
  };

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

// Common toast messages
export const TOAST_MESSAGES = {
  PASSWORD_RESET_SUCCESS: 'Password reset link sent to your email',
  PASSWORD_RESET_ERROR: 'Failed to send password reset email',
  LOGIN_SUCCESS: 'Successfully logged in',
  LOGIN_ERROR: 'Login failed',
  LOGOUT_SUCCESS: 'Successfully logged out',
  SAVE_SUCCESS: 'Changes saved successfully',
  SAVE_ERROR: 'Failed to save changes',
  DELETE_SUCCESS: 'Item deleted successfully',
  DELETE_ERROR: 'Failed to delete item',
  UPDATE_SUCCESS: 'Updated successfully',
  UPDATE_ERROR: 'Failed to update',
  CREATE_SUCCESS: 'Created successfully',
  CREATE_ERROR: 'Failed to create',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action',
  VALIDATION_ERROR: 'Please check your input and try again',
  // Rule specific messages
  SUCCESS_DELETE_RULE: 'Rule deleted successfully',
  ERROR_DELETE_RULE: 'Failed to delete rule',
  SUCCESS_CREATE_RULE: 'Rule created successfully',
  ERROR_CREATE_RULE: 'Failed to create rule',
  SUCCESS_UPDATE_RULE: 'Rule updated successfully',
  ERROR_UPDATE_RULE: 'Failed to update rule',
} as const;

/**
 * Toast Utility Usage Examples:
 *
 * 1. Basic Usage:
 * ```tsx
 * import { useToastHelpers, TOAST_MESSAGES } from '@/utils/toast';
 *
 * const MyComponent = () => {
 *   const { showSuccess, showError, showWarning, showInfo } = useToastHelpers();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       showSuccess(TOAST_MESSAGES.SAVE_SUCCESS);
 *     } catch (error) {
 *       showError(TOAST_MESSAGES.SAVE_ERROR);
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * };
 * ```
 *
 * 2. Custom Messages:
 * ```tsx
 * const handleCustomAction = () => {
 *   showSuccess('Your custom success message');
 *   showError('Your custom error message');
 *   showWarning('Your custom warning message');
 *   showInfo('Your custom info message');
 * };
 * ```
 *
 * 3. Custom Duration:
 * ```tsx
 * const handleQuickNotification = () => {
 *   showSuccess('Quick notification', 2000); // 2 seconds
 * };
 * ```
 *
 * 4. Error Handling:
 * ```tsx
 * const handleApiCall = async () => {
 *   try {
 *     const response = await apiCall();
 *     if (response.success) {
 *       showSuccess(TOAST_MESSAGES.UPDATE_SUCCESS);
 *     } else {
 *       showError(response.message || TOAST_MESSAGES.UPDATE_ERROR);
 *     }
 *   } catch (error) {
 *     const errorMessage = error instanceof Error ? error.message : 'Unknown error';
 *     showError(`${TOAST_MESSAGES.NETWORK_ERROR}: ${errorMessage}`);
 *   }
 * };
 * ```
 */
